/**
 * /me/loans/active — Session 3d PR 15.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { ActiveLoanItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyActiveLoansPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-active-loans", employeeId],
    queryFn: () => session3dApi.listMyActiveLoans(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<ActiveLoanItem>[] = [
    {
      headerKey: "me.active-loans.col.product",
      label: "Product",
      render: (r) => <span className="font-medium">{r.product_name_en}</span>,
    },
    {
      headerKey: "me.active-loans.col.outstanding",
      label: "Outstanding (SAR)",
      render: (r) => <span className="font-mono font-semibold">{r.outstanding_sar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
      traceText: (r) => r.trace_outstanding,
    },
    {
      headerKey: "me.active-loans.col.monthly",
      label: "Monthly installment",
      render: (r) => <span className="font-mono text-xs">{r.monthly_installment_sar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
    },
    {
      headerKey: "me.active-loans.col.remaining",
      label: "Months remaining",
      render: (r) => <span className="font-mono text-xs">{r.months_remaining}</span>,
    },
    {
      headerKey: "me.active-loans.col.next_payment",
      label: "Next payment",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.next_payment_date}</span>,
    },
  ];

  return (
    <MeListPage<ActiveLoanItem>
      title={t("me.active-loans.title", "My Active Loans")}
      subtitle={t("me.active-loans.subtitle", "Currently in-repayment loans with outstanding + remaining installments.")}
      domain="active-loans"
      khidmatPrefill={t("me.active-loans.ask_khidmat", "Show me my current loan balances and remaining payments.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.application_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.active-loans.title", "My Active Loans"),
        window_label: t("me.active-loans.window", "in repayment"),
      }}
      emptyHeadline={t("me.active-loans.empty_headline", "No active loans.")}
      emptyBody={t("me.active-loans.empty_body", "Khidmat: nice — you're loan-free.")}
    />
  );
}
